import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { database } from '../../firebase';
import { ref, get, set, push, update } from 'firebase/database';
import Swal from 'sweetalert2';
import {
  MagnifyingGlassIcon, XMarkIcon, PlusIcon, MinusIcon,
  TrashIcon, TagIcon, ShoppingCartIcon, PrinterIcon,
  ArrowPathIcon, CubeIcon, CheckCircleIcon, UserIcon,
  PhoneIcon, SparklesIcon, ReceiptRefundIcon,
  ChevronDownIcon, FolderIcon,
  CreditCardIcon, BanknotesIcon, ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import {
  ShoppingCartIcon as CartSolid,
  CheckCircleIcon as CheckSolid,
} from '@heroicons/react/24/solid';

if (typeof document !== 'undefined' && !document.getElementById('swal-sale-z')) {
  const s = document.createElement('style');
  s.id = 'swal-sale-z';
  s.textContent = `.swal2-container{z-index:999999!important}.swal2-on-top{z-index:999999!important}`;
  document.head.appendChild(s);
}
const toast = (icon, title, text = '') =>
  Swal.fire({ icon, title, text, timer: 2500, toast: true, position: 'top-end', showConfirmButton: false, customClass: { container: 'swal2-on-top' } });

const WhatsAppIcon = ({ className = 'h-5 w-5' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const fmtNum = (n) => (n ?? 0).toLocaleString();

const isOfferActive = (offer) => {
  if (!offer || !offer.isActive) return false;
  const now = Date.now();
  if (!offer.startDate && !offer.endDate) return true;
  if (offer.startDate && offer.endDate)   return now >= offer.startDate && now <= offer.endDate;
  if (offer.startDate && !offer.endDate)  return now >= offer.startDate;
  if (!offer.startDate && offer.endDate)  return now <= offer.endDate;
  return true;
};

const getBasePrice = (product) => {
  if (isOfferActive(product.offer) && product.offer.price < product.price) return product.offer.price;
  return product.price;
};

const getTotalStock = (variants = [], type) => {
  if (!variants?.length) return 0;
  if (type === 'simple')     return variants[0]?.quantity || 0;
  if (type === 'size_only')  return variants.reduce((s, v) => s + (v.quantity || 0), 0);
  if (type === 'color_size') return variants.reduce((s, v) =>
    s + (v.sizes || []).reduce((ss, sz) => ss + (sz.quantity || 0), 0), 0);
  return 0;
};

const generateSaleNumber = async () => {
  try {
    const counterRef = ref(database, 'system/counters/saleNumber');
    const snap = await get(counterRef);
    const next = (snap.exists() ? snap.val() : 0) + 1;
    await set(counterRef, next);
    return `SALE-${String(next).padStart(4, '0')}`;
  } catch {
    return `SALE-${Date.now()}`;
  }
};

// ─── VARIANT PICKER ───────────────────────────────────────────────────────────
const VariantPicker = ({ product, onSelect, onClose }) => {
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedSize,  setSelectedSize]  = useState('');
  const [quantity,      setQuantity]      = useState(1);
  const variants = product.variants || [];
  const type     = product.variantType;

  const getMaxQty = () => {
    if (type === 'simple')    return variants[0]?.quantity || 0;
    if (type === 'size_only') { const v = variants.find(v => v.size === selectedSize); return v?.quantity || 0; }
    if (type === 'color_size') { const v = variants.find(v => v.color === selectedColor); const s = v?.sizes?.find(s => s.size === selectedSize); return s?.quantity || 0; }
    return 0;
  };

  const canAdd = () => {
    if (type === 'simple')     return quantity > 0 && quantity <= (variants[0]?.quantity || 0);
    if (type === 'size_only')  return selectedSize && quantity > 0 && quantity <= getMaxQty();
    if (type === 'color_size') return selectedColor && selectedSize && quantity > 0 && quantity <= getMaxQty();
    return false;
  };

  const handleAdd = () => {
    if (!canAdd()) return;
    onSelect({ ...product, selectedColor, selectedSize, quantity, variantKey: type === 'simple' ? '0' : type === 'size_only' ? selectedSize : `${selectedColor}_${selectedSize}` });
    onClose();
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: 199999, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}>
      <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
        className="bg-white rounded-2xl shadow-2xl w-full overflow-hidden"
        style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4" style={{ background: 'linear-gradient(135deg,#1a1a2e,#800020)' }}>
          <div className="flex items-center gap-3">
            {product.images?.[0] && <img src={product.images[0]} alt="" className="w-10 h-10 rounded-xl object-contain bg-white p-0.5" />}
            <div>
              <h3 className="text-sm font-extrabold text-white truncate max-w-[220px]">{product.name}</h3>
              <p className="text-white/60 text-xs">KES {fmtNum(getBasePrice(product))}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl text-white" style={{ background: 'rgba(255,255,255,0.15)' }}>
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {type === 'color_size' && (
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Select Color</label>
              <div className="flex flex-wrap gap-2">
                {variants.map((v, i) => (
                  <button key={i} onClick={() => { setSelectedColor(v.color); setSelectedSize(''); }}
                    className="px-3 py-1.5 rounded-lg border-2 text-xs font-semibold transition-all"
                    style={{ borderColor: selectedColor === v.color ? '#800020' : '#e5e7eb', background: selectedColor === v.color ? '#fef2f2' : '#fff', color: selectedColor === v.color ? '#800020' : '#6b7280' }}>
                    {v.color}
                  </button>
                ))}
              </div>
            </div>
          )}
          {(type === 'size_only' || (type === 'color_size' && selectedColor)) && (
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Select Size</label>
              <div className="flex flex-wrap gap-2">
                {(type === 'size_only' ? variants : variants.find(v => v.color === selectedColor)?.sizes || []).map((v, i) => (
                  <button key={i} onClick={() => setSelectedSize(v.size)} disabled={v.quantity === 0}
                    className="px-3 py-1.5 rounded-lg border-2 text-xs font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ borderColor: selectedSize === v.size ? '#800020' : '#e5e7eb', background: selectedSize === v.size ? '#fef2f2' : '#fff', color: selectedSize === v.size ? '#800020' : '#6b7280' }}>
                    {v.size} <span className="opacity-50">({v.quantity})</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {type === 'simple' && (
            <div className="p-3 bg-gray-50 rounded-xl text-sm text-gray-600">
              Available: <strong>{variants[0]?.quantity || 0} {variants[0]?.unit || 'pcs'}</strong>
            </div>
          )}
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Quantity</label>
            <div className="flex items-center gap-3">
              <button onClick={() => setQuantity(q => Math.max(1, q - 1))} className="w-10 h-10 flex items-center justify-center rounded-xl border-2 border-gray-200 text-gray-600 hover:border-gray-300 transition-all"><MinusIcon className="h-4 w-4" /></button>
              <input type="number" min="1" max={getMaxQty()} value={quantity} onChange={e => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-20 text-center border-2 border-gray-200 rounded-xl py-2 text-sm font-bold focus:outline-none"
                onFocus={e => e.target.style.borderColor = '#800020'} onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
              <button onClick={() => setQuantity(q => Math.min(getMaxQty(), q + 1))} className="w-10 h-10 flex items-center justify-center rounded-xl border-2 border-gray-200 text-gray-600 hover:border-gray-300 transition-all"><PlusIcon className="h-4 w-4" /></button>
              {getMaxQty() > 0 && <span className="text-xs text-gray-400">max {getMaxQty()}</span>}
            </div>
          </div>
          <div className="p-3 rounded-xl flex justify-between items-center" style={{ background: '#fef2f2' }}>
            <span className="text-sm text-gray-600">Subtotal</span>
            <span className="text-base font-extrabold" style={{ color: '#800020' }}>KES {fmtNum(getBasePrice(product) * quantity)}</span>
          </div>
          <button onClick={handleAdd} disabled={!canAdd()} className="w-full py-3 rounded-2xl text-sm font-bold text-white shadow transition-all disabled:opacity-40 disabled:cursor-not-allowed" style={{ background: 'linear-gradient(135deg,#800020,#b30000)' }}>
            Add to Sale
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─── RECEIPT MODAL ────────────────────────────────────────────────────────────
const ReceiptModal = ({ sale, onClose }) => {
  const printReceipt = () => {
    const dateStr = new Date(sale.createdAt).toLocaleDateString('en-KE', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    const itemRows = sale.items.map(item => `
      <tr>
        <td style="padding:6px 4px;border-bottom:1px solid #f1f5f9">${item.name}${item.selectedColor ? `<br/><span style="font-size:10px;color:#9ca3af">Color: ${item.selectedColor}</span>` : ''}${item.selectedSize ? `<span style="font-size:10px;color:#9ca3af"> · Size: ${item.selectedSize}</span>` : ''}</td>
        <td style="padding:6px 4px;border-bottom:1px solid #f1f5f9;text-align:center">${item.quantity}</td>
        <td style="padding:6px 4px;border-bottom:1px solid #f1f5f9;text-align:right">KES ${fmtNum(item.unitPrice)}</td>
        <td style="padding:6px 4px;border-bottom:1px solid #f1f5f9;text-align:right;font-weight:600">KES ${fmtNum(item.unitPrice * item.quantity)}</td>
      </tr>`).join('');
    // Payment rows for print
    const payRows = (sale.payments || []).map(p => `
      <tr>
        <td style="padding:4px;font-size:11px;text-transform:capitalize">${p.method}</td>
        <td style="padding:4px;font-size:11px;text-align:center">${p.status === 'paid' ? '✓ Paid' : '🕐 Pending'}</td>
        <td style="padding:4px;font-size:11px;text-align:right;font-weight:600">KES ${fmtNum(p.amount)}</td>
      </tr>`).join('');
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"/><title>Receipt ${sale.saleNumber}</title>
    <style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:12px;color:#1a1a2e;padding:24px;max-width:400px;margin:0 auto}
    .header{text-align:center;margin-bottom:20px;padding-bottom:16px;border-bottom:2px dashed #e5e7eb}.logo{font-size:20px;font-weight:900;color:#800020}.sub{font-size:11px;color:#9ca3af;margin-top:2px}
    .receipt-no{font-size:13px;font-weight:700;margin-top:8px}.date{font-size:11px;color:#9ca3af}.customer{background:#f8f9fa;border-radius:8px;padding:10px;margin:12px 0}
    table{width:100%;border-collapse:collapse;font-size:11px}thead tr{background:#1a1a2e;color:#fff}thead th{padding:7px 4px;text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:.05em}
    .total-row td{padding:8px 4px;color:#800020;font-weight:700;font-size:13px;background:#fef2f2}.footer{margin-top:20px;text-align:center;font-size:10px;color:#9ca3af;padding-top:12px;border-top:1px dashed #e5e7eb}
    @media print{body{padding:8mm}}</style></head><body>
    <div class="header"><div class="logo">POWER<span style="color:#1a1a2e">CORE</span></div><div class="sub">FITNESS</div><div class="receipt-no">${sale.saleNumber}</div><div class="date">${dateStr}</div>${sale.servedBy ? `<div class="date">Served by: ${sale.servedBy}</div>` : ''}</div>
    ${sale.customerName || sale.customerPhone ? `<div class="customer"><p style="font-size:11px;color:#6b7280;margin-bottom:2px">Customer</p><strong style="font-size:13px">${sale.customerName || 'Walk-in'}</strong>${sale.customerPhone ? `<br/><span style="font-size:11px;color:#9ca3af">${sale.customerPhone}</span>` : ''}</div>` : ''}
    <table><thead><tr><th>Item</th><th style="text-align:center">Qty</th><th style="text-align:right">Price</th><th style="text-align:right">Total</th></tr></thead>
    <tbody>${itemRows}${sale.discount > 0 ? `<tr><td colspan="3" style="padding:6px 4px;text-align:right;color:#16a34a;font-weight:600">Discount</td><td style="padding:6px 4px;text-align:right;color:#16a34a;font-weight:600">- KES ${fmtNum(sale.discount)}</td></tr>` : ''}
    <tr class="total-row"><td colspan="3" style="text-align:right">TOTAL</td><td style="text-align:right">KES ${fmtNum(sale.total)}</td></tr></tbody></table>
    ${payRows ? `<div style="margin-top:12px"><p style="font-size:10px;font-weight:700;color:#9ca3af;text-transform:uppercase;margin-bottom:6px">Payment</p><table><tbody>${payRows}</tbody></table></div>` : ''}
    <div class="footer"><p>Thank you for shopping with us!</p><p style="margin-top:4px">PowerCore Fitness · Nairobi</p></div>
    <script>window.onload=()=>window.print()</script></body></html>`;
    const w = window.open('', '_blank'); w.document.write(html); w.document.close();
  };

  const shareWhatsApp = () => {
    if (!sale.customerPhone) { toast('warning', 'No phone number', 'Add customer phone to share via WhatsApp'); return; }
    const items    = sale.items.map(i => `  • ${i.name}${i.selectedSize ? ` (${i.selectedSize})` : ''} x${i.quantity} — KES ${fmtNum(i.unitPrice * i.quantity)}`).join('%0A');
    const payLines = (sale.payments || []).map(p => `  ${p.method === 'cash' ? '💵' : '💳'} ${p.method.charAt(0).toUpperCase() + p.method.slice(1)}: KES ${fmtNum(p.amount)} (${p.status})`).join('%0A');
    const msg = `🧾 *Receipt: ${sale.saleNumber}*%0A══════════════════════%0A📅 ${new Date(sale.createdAt).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' })}%0A%0A📦 *Items:*%0A${items}%0A%0A${sale.discount > 0 ? `💸 Discount: - KES ${fmtNum(sale.discount)}%0A` : ''}💰 *Total: KES ${fmtNum(sale.total)}*%0A%0A*Payment:*%0A${payLines}%0A%0AThank you for shopping at PowerCore Fitness! 💪`;
    const phone = sale.customerPhone.replace(/\D/g, '').replace(/^0/, '254');
    window.open(`https://wa.me/${phone}?text=${msg}`, '_blank');
  };

  const paid    = (sale.payments || []).filter(p => p.status === 'paid').reduce((s, p) => s + p.amount, 0);
  const pending = sale.total - paid;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: 199999, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}>
      <motion.div initial={{ scale: 0.92 }} animate={{ scale: 1 }} exit={{ scale: 0.92 }}
        className="bg-white rounded-2xl shadow-2xl w-full overflow-hidden flex flex-col"
        style={{ maxWidth: 480, maxHeight: '90dvh' }} onClick={e => e.stopPropagation()}>
        <div className="flex-shrink-0 flex items-center justify-between px-5 py-4" style={{ background: 'linear-gradient(135deg,#15803d,#16a34a)' }}>
          <div className="flex items-center gap-2">
            <CheckSolid className="h-6 w-6 text-white" />
            <div><h3 className="text-base font-extrabold text-white">Sale Complete!</h3><p className="text-white/60 text-xs">{sale.saleNumber}</p></div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl text-white" style={{ background: 'rgba(255,255,255,0.15)' }}><XMarkIcon className="h-4 w-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          <div className="border border-gray-100 rounded-2xl overflow-hidden">
            <div className="text-center py-5 px-4" style={{ background: '#fafafa', borderBottom: '1px dashed #e5e7eb' }}>
              <p className="text-xl font-black tracking-tight text-gray-900">POWER<span style={{ color: '#800020' }}>CORE</span></p>
              <p className="text-[10px] font-light tracking-[0.3em] uppercase text-gray-400 mt-0.5">Fitness</p>
              <p className="text-sm font-bold text-gray-700 mt-2">{sale.saleNumber}</p>
              <p className="text-xs text-gray-400">{new Date(sale.createdAt).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
            </div>
            {(sale.customerName || sale.customerPhone) && (
              <div className="px-4 py-3" style={{ background: '#f8f9fa', borderBottom: '1px solid #f1f5f9' }}>
                <p className="text-xs text-gray-400 mb-0.5">Customer</p>
                <p className="text-sm font-semibold text-gray-800">{sale.customerName || 'Walk-in'}</p>
                {sale.customerPhone && <p className="text-xs text-gray-500">{sale.customerPhone}</p>}
              </div>
            )}
            <div className="px-4 py-3 space-y-2.5">
              {sale.items.map((item, i) => (
                <div key={i} className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-800">{item.name}</p>
                    {(item.selectedColor || item.selectedSize) && <p className="text-[10px] text-gray-400">{item.selectedColor}{item.selectedColor && item.selectedSize ? ' · ' : ''}{item.selectedSize}</p>}
                    <p className="text-[10px] text-gray-400">x{item.quantity} @ KES {fmtNum(item.unitPrice)}</p>
                  </div>
                  <p className="text-xs font-bold flex-shrink-0" style={{ color: '#800020' }}>KES {fmtNum(item.unitPrice * item.quantity)}</p>
                </div>
              ))}
            </div>
            <div className="px-4 py-3 space-y-1.5" style={{ borderTop: '1px dashed #e5e7eb', background: '#fafafa' }}>
              {sale.discount > 0 && <div className="flex justify-between text-xs text-green-600 font-semibold"><span>Discount</span><span>- KES {fmtNum(sale.discount)}</span></div>}
              <div className="flex justify-between items-center">
                <span className="text-sm font-extrabold text-gray-900">TOTAL</span>
                <span className="text-lg font-extrabold" style={{ color: '#800020' }}>KES {fmtNum(sale.total)}</span>
              </div>
            </div>
            {/* Payment breakdown */}
            <div className="px-4 py-3" style={{ borderTop: '1px solid #f1f5f9' }}>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Payment</p>
              <div className="space-y-1.5">
                {(sale.payments || []).map((p, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      {p.method === 'cash'
                        ? <BanknotesIcon className="h-3.5 w-3.5 text-green-600" />
                        : <CreditCardIcon className="h-3.5 w-3.5 text-blue-500" />}
                      <span className="text-xs font-semibold capitalize" style={{ color: p.method === 'cash' ? '#16a34a' : '#3b82f6' }}>{p.method}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${p.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                        {p.status === 'paid' ? '✓ Paid' : '🕐 Pending'}
                      </span>
                      {p.reference && <span className="text-[10px] text-gray-400">· {p.reference}</span>}
                    </div>
                    <span className="text-xs font-bold" style={{ color: p.method === 'cash' ? '#16a34a' : '#3b82f6' }}>KES {fmtNum(p.amount)}</span>
                  </div>
                ))}
                {pending > 0 && (
                  <div className="flex items-center justify-between pt-1 border-t border-dashed border-amber-200">
                    <span className="text-xs text-amber-700 font-semibold flex items-center gap-1">
                      <ExclamationTriangleIcon className="h-3.5 w-3.5" />Balance pending
                    </span>
                    <span className="text-xs font-extrabold text-amber-700">KES {fmtNum(pending)}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="text-center py-3 px-4 text-[10px] text-gray-400" style={{ borderTop: '1px dashed #e5e7eb' }}>Thank you for shopping with us! 💪</div>
          </div>
        </div>
        <div className="flex-shrink-0 px-5 pb-5 pt-3 border-t border-gray-100 grid grid-cols-2 gap-3">
          <button onClick={printReceipt} className="flex items-center justify-center gap-2 py-3 rounded-2xl text-sm font-bold text-white shadow" style={{ background: 'linear-gradient(135deg,#1a1a2e,#374151)' }}>
            <PrinterIcon className="h-4 w-4" />Print
          </button>
          <button onClick={shareWhatsApp} className="flex items-center justify-center gap-2 py-3 rounded-2xl text-sm font-bold text-white shadow" style={{ background: '#25D366' }}
            onMouseEnter={e => e.currentTarget.style.background = '#1da851'} onMouseLeave={e => e.currentTarget.style.background = '#25D366'}>
            <WhatsAppIcon className="h-4 w-4" />WhatsApp
          </button>
          <button onClick={onClose} className="col-span-2 py-2.5 rounded-2xl text-sm font-semibold border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all">Close</button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
const Sale = () => {
  const { userData } = useAuth();
  const userRole     = (userData?.personalInfo?.role || '').toLowerCase();
  const isAuthorized = userRole === 'admin' || userRole === 'superadmin';
  const servedBy     = userData?.personalInfo?.name || 'Staff';

  const [products,   setProducts]   = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [cartItems,  setCartItems]  = useState([]);
  const [customerName,  setCustomerName]  = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [discount,      setDiscount]      = useState(0);
  const [search,        setSearch]        = useState('');
  const [filterCat,     setFilterCat]     = useState('');
  const [catDropOpen,   setCatDropOpen]   = useState(false);
  const [variantPickerProduct, setVariantPickerProduct] = useState(null);
  const [completedSale,        setCompletedSale]        = useState(null);
  const [processing,           setProcessing]           = useState(false);
  const [pendingSale,          setPendingSale]          = useState(null); // partial: saved sale awaiting online completion

  // ── Payment state ──────────────────────────────────────────────────────────
  // mode: 'cash' | 'online' | 'partial'
  const [payMode,      setPayMode]      = useState('cash');
  const [cashAmount,   setCashAmount]   = useState('');   // partial: cash portion
  const [cashRef,      setCashRef]      = useState('');   // cash received (for change calc)
  const [onlineRef,    setOnlineRef]    = useState('');   // online reference

  const catDropRef = useRef();

  const resetPayment = () => { setPayMode('cash'); setCashAmount(''); setCashRef(''); setOnlineRef(''); };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [pSnap, cSnap] = await Promise.all([get(ref(database, 'products')), get(ref(database, 'categories'))]);
      setProducts(pSnap.exists() ? Object.entries(pSnap.val()).map(([id, d]) => ({ id, ...d })).filter(p => p.status === 'active' && getTotalStock(p.variants, p.variantType) > 0) : []);
      setCategories(cSnap.exists() ? Object.entries(cSnap.val()).map(([id, d]) => ({ id, ...d })) : []);
    } catch (e) { console.error(e); toast('error', 'Failed to load products'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => {
    const h = (e) => { if (catDropRef.current && !catDropRef.current.contains(e.target)) setCatDropOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const mainCats = categories.filter(c => c.level === 0 && !c.parentId);
  const catName  = (id) => categories.find(c => c.id === id)?.name || '';

  const filteredProducts = products.filter(p => {
    if (filterCat) {
      const catIds = new Set([filterCat]);
      categories.filter(c => c.parentId === filterCat).forEach(s => { catIds.add(s.id); categories.filter(c => c.parentId === s.id).forEach(ss => catIds.add(ss.id)); });
      if (!catIds.has(p.categoryId) && !catIds.has(p.subCategoryId) && !catIds.has(p.subSubCategoryId)) return false;
    }
    if (search.trim()) { const q = search.toLowerCase(); if (!p.name?.toLowerCase().includes(q) && !p.brand?.toLowerCase().includes(q) && !p.sku?.toLowerCase().includes(q)) return false; }
    return true;
  });

  const subtotal = cartItems.reduce((s, i) => s + i.unitPrice * i.quantity, 0);
  const total    = Math.max(0, subtotal - (discount || 0));

  // ── Derived payment values ─────────────────────────────────────────────────
  const cashNum     = Math.min(Number(cashAmount) || 0, total);
  const onlineNum   = Math.max(0, total - cashNum);           // remainder goes online
  const cashReceived = Number(cashRef) || 0;
  const changeAmount = payMode === 'cash' && cashReceived > total ? cashReceived - total : 0;
  const partialOwed  = payMode === 'partial' ? Math.max(0, total - cashNum) : 0;

  // ── Build payments array ───────────────────────────────────────────────────
  const buildPayments = () => {
    if (payMode === 'cash') {
      return [{ method: 'cash', amount: total, reference: cashRef || '', status: 'paid' }];
    }
    if (payMode === 'online') {
      return [{ method: 'online', amount: total, reference: onlineRef || '', status: 'pending' }];
    }
    // partial: cash portion paid now, online remainder pending
    const arr = [];
    if (cashNum > 0)    arr.push({ method: 'cash',   amount: cashNum,   reference: cashRef   || '', status: 'paid'    });
    if (onlineNum > 0)  arr.push({ method: 'online',  amount: onlineNum, reference: onlineRef || '', status: 'pending' });
    return arr;
  };

  const addToCart = (product) => {
    if (product.variantType === 'simple') {
      const maxQty = product.variants?.[0]?.quantity || 0;
      if (maxQty === 0) { toast('warning', 'Out of stock'); return; }
      setCartItems(prev => {
        const existing = prev.find(i => i.productId === product.id && !i.selectedColor && !i.selectedSize);
        if (existing) { if (existing.quantity >= maxQty) { toast('warning', 'Max stock reached'); return prev; } return prev.map(i => i === existing ? { ...i, quantity: i.quantity + 1 } : i); }
        return [...prev, { cartKey: `${product.id}_simple`, productId: product.id, name: product.name, image: product.images?.[0] || null, unitPrice: getBasePrice(product), quantity: 1, selectedColor: '', selectedSize: '', variantType: 'simple' }];
      });
      return;
    }
    setVariantPickerProduct(product);
  };

  const handleVariantSelected = (p) => {
    const cartKey = `${p.id}_${p.variantKey}`;
    const maxQty  = p.quantity;
    setCartItems(prev => {
      const existing = prev.find(i => i.cartKey === cartKey);
      if (existing) { if (existing.quantity >= maxQty) { toast('warning', 'Max stock reached'); return prev; } return prev.map(i => i.cartKey === cartKey ? { ...i, quantity: i.quantity + 1 } : i); }
      return [...prev, { cartKey, productId: p.id, name: p.name, image: p.images?.[0] || null, unitPrice: getBasePrice(p), quantity: p.quantity, selectedColor: p.selectedColor || '', selectedSize: p.selectedSize || '', variantType: p.variantType, maxQty }];
    });
  };

  const updateCartQty = (cartKey, delta) => setCartItems(prev => prev.map(i => { if (i.cartKey !== cartKey) return i; const n = i.quantity + delta; if (n <= 0) return null; return { ...i, quantity: n }; }).filter(Boolean));
  const removeFromCart = (cartKey) => setCartItems(prev => prev.filter(i => i.cartKey !== cartKey));
  const clearCart = () => { setCartItems([]); setCustomerName(''); setCustomerPhone(''); setDiscount(0); resetPayment(); };

  // ── ONLINE: show coming soon Swal, no sale recorded ─────────────────────────
  const handleOnlineClick = async () => {
    await Swal.fire({
      title: '💳 Online Payment',
      html: `
        <div style="text-align:center;padding:8px 0 4px">
          <div style="font-size:48px;margin-bottom:12px">🚧</div>
          <p style="color:#374151;font-size:14px;line-height:1.6;margin-bottom:8px">
            Online payment via <strong>Paystack</strong> is being integrated and will be available very soon.
          </p>
          <p style="font-size:12px;color:#6b7280">Please use Cash or Partial payment for now.</p>
        </div>
      `,
      icon: undefined,
      confirmButtonText: 'Got it',
      confirmButtonColor: '#800020',
      customClass: { container: 'swal2-on-top', popup: 'rounded-2xl' },
    });
  };

  // ── PARTIAL STEP 1: save cash portion, mark online as pending ─────────────
  const recordCashPortion = async () => {
    if (!cartItems.length) { toast('warning', 'Cart is empty'); return; }
    if (cashNum <= 0) { toast('warning', 'Enter cash amount', 'Set how much cash was paid now'); return; }
    setProcessing(true);
    try {
      const saleNumber = await generateSaleNumber();
      const now        = Date.now();
      const payments   = [
        { method: 'cash',   amount: cashNum,   reference: cashRef || '', status: 'paid'    },
        { method: 'online', amount: onlineNum, reference: '',            status: 'pending' },
      ];
      const saleRecord = {
        saleNumber,
        createdAt:     now,
        servedBy,
        servedById:    userData?.uid || null,
        customerName:  customerName  || '',
        customerPhone: customerPhone || '',
        paymentMode:   'partial',
        payments,
        paymentStatus: 'partial',
        discount:      discount || 0,
        subtotal,
        total,
        items: cartItems.map(i => ({
          productId: i.productId, name: i.name, image: i.image || null,
          unitPrice: i.unitPrice, quantity: i.quantity, subtotal: i.unitPrice * i.quantity,
          selectedColor: i.selectedColor || '', selectedSize: i.selectedSize || '', variantType: i.variantType,
        })),
      };
      const saleRef = push(ref(database, 'sales'));
      await set(saleRef, { ...saleRecord, id: saleRef.key });
      // Decrement stock immediately
      for (const item of cartItems) {
        try {
          const pRef = ref(database, `products/${item.productId}`); const pSnap = await get(pRef); if (!pSnap.exists()) continue;
          const prod = pSnap.val(); const variants = JSON.parse(JSON.stringify(prod.variants || []));
          if (item.variantType === 'simple' && variants[0]) { variants[0].quantity = Math.max(0, (variants[0].quantity || 0) - item.quantity); }
          else if (item.variantType === 'size_only') { const vi = variants.findIndex(v => v.size === item.selectedSize); if (vi >= 0) variants[vi].quantity = Math.max(0, (variants[vi].quantity || 0) - item.quantity); }
          else if (item.variantType === 'color_size') { const vi = variants.findIndex(v => v.color === item.selectedColor); if (vi >= 0) { const si = (variants[vi].sizes || []).findIndex(s => s.size === item.selectedSize); if (si >= 0) variants[vi].sizes[si].quantity = Math.max(0, (variants[vi].sizes[si].quantity || 0) - item.quantity); } }
          await update(pRef, { variants, totalSales: (prod.totalSales || 0) + item.quantity, updatedAt: now });
        } catch (e) { console.error(`Stock error:`, e); }
      }
      const saved = { ...saleRecord, id: saleRef.key };
      setPendingSale(saved);
      clearCart();
      loadData();
      toast('success', `${saleNumber} — cash portion saved`, `KES ${fmtNum(onlineNum)} remaining via online`);
    } catch (e) { console.error(e); toast('error', 'Could not save sale'); }
    finally { setProcessing(false); }
  };

  // ── PARTIAL STEP 2: complete remainder via Paystack (coming soon) ──────────
  const completeOnlineRemainder = async () => {
    if (!pendingSale) return;
    await Swal.fire({
      title: '💳 Complete with Paystack',
      html: `
        <div style="text-align:center;padding:8px 0 4px">
          <div style="font-size:48px;margin-bottom:12px">🚧</div>
          <p style="color:#374151;font-size:14px;line-height:1.6;margin-bottom:8px">
            Paystack integration is coming soon.
          </p>
          <div style="background:#fffbeb;border:1px solid #fde68a;border-radius:12px;padding:12px;text-align:left">
            <p style="font-size:12px;color:#92400e;font-weight:600;margin-bottom:4px">Sale: ${pendingSale.saleNumber}</p>
            <p style="font-size:12px;color:#92400e">Remainder: <strong>KES ${fmtNum(onlineNum || (pendingSale.payments?.find(p => p.method === 'online')?.amount || 0))}</strong></p>
            <p style="font-size:12px;color:#92400e;margin-top:4px">Cash already collected: KES ${fmtNum(pendingSale.payments?.find(p => p.method === 'cash')?.amount || 0)}</p>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Mark as Paid (manual)',
      cancelButtonText: 'Close',
      confirmButtonColor: '#800020',
      cancelButtonColor: '#6b7280',
      customClass: { container: 'swal2-on-top', popup: 'rounded-2xl' },
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      // Admin manually confirms online payment — update sale to fully paid
      try {
        const onlineAmt = pendingSale.payments?.find(p => p.method === 'online')?.amount || 0;
        const updatedPayments = pendingSale.payments.map(p =>
          p.method === 'online' ? { ...p, status: 'paid', completedAt: Date.now() } : p
        );
        await update(ref(database, `sales/${pendingSale.id}`), {
          payments:      updatedPayments,
          paymentStatus: 'paid',
          completedAt:   Date.now(),
        });
        const fullyPaid = { ...pendingSale, payments: updatedPayments, paymentStatus: 'paid' };
        setPendingSale(null);
        setCompletedSale(fullyPaid);
        toast('success', 'Payment completed!', `${pendingSale.saleNumber} fully paid`);
      } catch (e) { console.error(e); toast('error', 'Could not update payment'); }
    });
  };

  // ── CASH: save full sale as paid ──────────────────────────────────────────
  const processSale = async () => {
    if (!cartItems.length) { toast('warning', 'Cart is empty'); return; }
    setProcessing(true);
    try {
      const saleNumber = await generateSaleNumber();
      const now        = Date.now();
      const payments   = [{ method: 'cash', amount: total, reference: cashRef || '', status: 'paid' }];
      const saleRecord = {
        saleNumber,
        createdAt:     now,
        servedBy,
        servedById:    userData?.uid || null,
        customerName:  customerName  || '',
        customerPhone: customerPhone || '',
        paymentMode:   'cash',
        payments,
        paymentStatus: 'paid',
        discount:      discount || 0,
        subtotal,
        total,
        items: cartItems.map(i => ({
          productId: i.productId, name: i.name, image: i.image || null,
          unitPrice: i.unitPrice, quantity: i.quantity, subtotal: i.unitPrice * i.quantity,
          selectedColor: i.selectedColor || '', selectedSize: i.selectedSize || '', variantType: i.variantType,
        })),
      };
      const saleRef = push(ref(database, 'sales'));
      await set(saleRef, { ...saleRecord, id: saleRef.key });
      for (const item of cartItems) {
        try {
          const pRef = ref(database, `products/${item.productId}`); const pSnap = await get(pRef); if (!pSnap.exists()) continue;
          const prod = pSnap.val(); const variants = JSON.parse(JSON.stringify(prod.variants || []));
          if (item.variantType === 'simple' && variants[0]) { variants[0].quantity = Math.max(0, (variants[0].quantity || 0) - item.quantity); }
          else if (item.variantType === 'size_only') { const vi = variants.findIndex(v => v.size === item.selectedSize); if (vi >= 0) variants[vi].quantity = Math.max(0, (variants[vi].quantity || 0) - item.quantity); }
          else if (item.variantType === 'color_size') { const vi = variants.findIndex(v => v.color === item.selectedColor); if (vi >= 0) { const si = (variants[vi].sizes || []).findIndex(s => s.size === item.selectedSize); if (si >= 0) variants[vi].sizes[si].quantity = Math.max(0, (variants[vi].sizes[si].quantity || 0) - item.quantity); } }
          await update(pRef, { variants, totalSales: (prod.totalSales || 0) + item.quantity, updatedAt: now });
        } catch (e) { console.error(`Stock error:`, e); }
      }
      setCompletedSale({ ...saleRecord, id: saleRef.key });
      clearCart(); loadData();
      toast('success', `${saleNumber} completed!`);
    } catch (e) { console.error(e); toast('error', 'Sale failed', e?.message || 'Please try again'); }
    finally { setProcessing(false); }
  };

  if (!isAuthorized) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-4" style={{ background: '#f8f9fa' }}>
      <ShoppingCartIcon className="h-16 w-16 text-gray-200" />
      <h2 className="text-xl font-bold text-gray-700">Access Denied</h2>
      <p className="text-gray-500 text-sm">Admin or superadmin privileges required.</p>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg,#f8f9fa 0%,#e9ecef 100%)' }}>
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#800020,#b30000)' }}><CartSolid className="h-5 w-5 text-white" /></div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-gray-900">New Sale</h1>
                <span className="px-2.5 py-1 rounded-lg text-xs font-bold" style={{ background: userRole === 'superadmin' ? '#1a1a2e' : '#fef2f2', color: userRole === 'superadmin' ? '#fff' : '#800020' }}>{userRole === 'superadmin' ? '⚡ Super Admin' : '🛡 Admin'}</span>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">Served by: {servedBy}</p>
            </div>
          </div>
          <button onClick={loadData} className="w-9 h-9 flex items-center justify-center rounded-xl bg-white border border-gray-200 text-gray-500 hover:text-gray-800 shadow-sm transition-all"><ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          {/* PRODUCT BROWSER — unchanged */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search products…"
                  className="w-full pl-10 pr-10 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none shadow-sm transition-all"
                  onFocus={e => e.target.style.borderColor = '#800020'} onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
                {search && <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"><XMarkIcon className="h-4 w-4" /></button>}
              </div>
              <div className="relative" ref={catDropRef}>
                <button onClick={() => setCatDropOpen(o => !o)}
                  className="flex items-center gap-2 px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 shadow-sm transition-all whitespace-nowrap"
                  style={{ borderColor: filterCat ? '#800020' : '#e5e7eb' }}>
                  <FolderIcon className="h-4 w-4 flex-shrink-0" style={{ color: filterCat ? '#800020' : '#9ca3af' }} />
                  <span className="max-w-[100px] truncate">{filterCat ? catName(filterCat) : 'All'}</span>
                  <ChevronDownIcon className={`h-3.5 w-3.5 text-gray-400 transition-transform ${catDropOpen ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {catDropOpen && (
                    <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                      className="absolute right-0 mt-1.5 w-52 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden" style={{ zIndex: 9999 }}>
                      <div className="max-h-64 overflow-y-auto p-1">
                        <button onClick={() => { setFilterCat(''); setCatDropOpen(false); }}
                          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all hover:bg-gray-50"
                          style={{ color: !filterCat ? '#800020' : '#374151', fontWeight: !filterCat ? 700 : 400 }}>
                          <SparklesIcon className="h-4 w-4" />All Categories
                        </button>
                        {mainCats.map(cat => (
                          <button key={cat.id} onClick={() => { setFilterCat(cat.id); setCatDropOpen(false); }}
                            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all hover:bg-gray-50"
                            style={{ color: filterCat === cat.id ? '#800020' : '#374151', fontWeight: filterCat === cat.id ? 700 : 400 }}>
                            {cat.imageUrl ? <img src={cat.imageUrl} alt="" className="w-4 h-4 rounded object-cover" /> : <FolderIcon className="h-4 w-4 text-gray-400" />}
                            <span className="truncate">{cat.name}</span>
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
            <p className="text-xs text-gray-400 font-medium">{filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''}</p>
            {loading ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-gray-100"><div className="inline-block w-8 h-8 border-4 border-red-200 border-t-red-700 rounded-full animate-spin" /><p className="text-gray-500 mt-3 text-sm">Loading…</p></div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-gray-100"><CubeIcon className="h-12 w-12 text-gray-200 mx-auto mb-3" /><p className="text-gray-500 text-sm">No products found</p><button onClick={() => { setSearch(''); setFilterCat(''); }} className="mt-2 text-sm font-semibold" style={{ color: '#800020' }}>Clear search</button></div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {filteredProducts.map(product => {
                  const stock  = getTotalStock(product.variants, product.variantType);
                  const price  = getBasePrice(product);
                  const inCart = cartItems.some(i => i.productId === product.id);
                  const onOffer = isOfferActive(product.offer) && product.offer.price < product.price;
                  return (
                    <motion.button key={product.id} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                      onClick={() => addToCart(product)}
                      className="bg-white rounded-xl border shadow-sm overflow-hidden text-left transition-all"
                      style={{ borderColor: inCart ? '#800020' : '#e5e7eb', borderWidth: inCart ? 2 : 1 }}>
                      <div className="relative bg-gray-50" style={{ height: 100 }}>
                        {product.images?.[0] ? <img src={product.images[0]} alt={product.name} className="w-full h-full object-contain p-2" /> : <div className="w-full h-full flex items-center justify-center"><CubeIcon className="h-8 w-8 text-gray-200" /></div>}
                        {onOffer && <span className="absolute top-1.5 left-1.5 text-[9px] font-extrabold text-white px-1.5 py-0.5 rounded" style={{ background: '#dc2626' }}>SALE</span>}
                        {inCart && <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: '#800020' }}><CheckCircleIcon className="h-3 w-3 text-white" /></div>}
                      </div>
                      <div className="p-2">
                        <p className="text-xs font-semibold text-gray-800 leading-tight line-clamp-2">{product.name}</p>
                        {product.brand && <p className="text-[10px] text-gray-400 mt-0.5">{product.brand}</p>}
                        <div className="flex items-center justify-between mt-1.5">
                          <span className="text-xs font-extrabold" style={{ color: '#800020' }}>KES {fmtNum(price)}</span>
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${stock > 5 ? 'bg-green-100 text-green-700' : stock > 0 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-600'}`}>{stock} left</span>
                        </div>
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            )}
          </div>

          {/* CART & CHECKOUT */}
          <div className="lg:col-span-1 space-y-4 sticky top-6">

            {/* ── PENDING PARTIAL SALE BANNER ── */}
            {pendingSale && (
              <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
                className="rounded-2xl p-4 border-2"
                style={{ borderColor: '#fde68a', background: '#fffbeb' }}>
                <div className="flex items-start gap-2 mb-3">
                  <ExclamationTriangleIcon className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-extrabold text-amber-800">{pendingSale.saleNumber} — Cash recorded</p>
                    <p className="text-[10px] text-amber-600 mt-0.5">
                      Cash: KES {fmtNum(pendingSale.payments?.find(p => p.method === 'cash')?.amount || 0)} paid ·{' '}
                      Online: KES {fmtNum(pendingSale.payments?.find(p => p.method === 'online')?.amount || 0)} pending
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                    onClick={completeOnlineRemainder}
                    className="flex-1 py-2.5 rounded-xl text-xs font-extrabold text-white flex items-center justify-center gap-1.5 shadow"
                    style={{ background: 'linear-gradient(135deg,#3b82f6,#2563eb)' }}>
                    <CreditCardIcon className="h-3.5 w-3.5" />
                    Complete · KES {fmtNum(pendingSale.payments?.find(p => p.method === 'online')?.amount || 0)}
                  </motion.button>
                  <button onClick={() => setPendingSale(null)}
                    className="px-3 py-2.5 rounded-xl text-xs font-semibold border border-amber-200 text-amber-700 hover:bg-amber-50 transition-all">
                    Dismiss
                  </button>
                </div>
              </motion.div>
            )}
            {/* Customer — unchanged */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2" style={{ background: 'linear-gradient(135deg,#f8f9fa,#fef2f2)' }}>
                <UserIcon className="h-4 w-4" style={{ color: '#800020' }} />
                <span className="text-sm font-extrabold text-gray-900">Customer (optional)</span>
              </div>
              <div className="p-4 space-y-3">
                <input value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="Customer name"
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none transition-all"
                  onFocus={e => e.target.style.borderColor = '#800020'} onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
                <div className="relative">
                  <PhoneIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                  <input value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} placeholder="Phone (for WhatsApp receipt)"
                    className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none transition-all"
                    onFocus={e => e.target.style.borderColor = '#800020'} onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
                </div>
              </div>
            </div>

            {/* Cart — unchanged */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between" style={{ background: 'linear-gradient(135deg,#f8f9fa,#fef2f2)' }}>
                <div className="flex items-center gap-2">
                  <CartSolid className="h-4 w-4" style={{ color: '#800020' }} />
                  <span className="text-sm font-extrabold text-gray-900">Cart {cartItems.length > 0 && `(${cartItems.reduce((s,i) => s + i.quantity, 0)} items)`}</span>
                </div>
                {cartItems.length > 0 && <button onClick={clearCart} className="text-xs font-semibold text-red-500 hover:text-red-700 transition-colors flex items-center gap-1"><TrashIcon className="h-3.5 w-3.5" />Clear</button>}
              </div>
              {cartItems.length === 0 ? (
                <div className="text-center py-10 px-4"><CartSolid className="h-8 w-8 text-gray-200 mx-auto mb-2" /><p className="text-xs text-gray-400">Tap a product to add it</p></div>
              ) : (
                <div className="divide-y divide-gray-50 max-h-64 overflow-y-auto">
                  {cartItems.map(item => (
                    <div key={item.cartKey} className="flex items-center gap-2 px-4 py-2.5">
                      <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {item.image ? <img src={item.image} alt="" className="w-full h-full object-contain p-0.5" /> : <CubeIcon className="h-4 w-4 text-gray-300" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-gray-800 truncate">{item.name}</p>
                        {(item.selectedColor || item.selectedSize) && <p className="text-[10px] text-gray-400">{item.selectedColor}{item.selectedColor && item.selectedSize ? ' · ' : ''}{item.selectedSize}</p>}
                        <p className="text-[10px] text-gray-400">KES {fmtNum(item.unitPrice)} ea.</p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button onClick={() => updateCartQty(item.cartKey, -1)} className="w-6 h-6 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:border-red-300 hover:text-red-500 transition-all"><MinusIcon className="h-3 w-3" /></button>
                        <span className="w-5 text-center text-xs font-bold">{item.quantity}</span>
                        <button onClick={() => updateCartQty(item.cartKey, 1)} className="w-6 h-6 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:border-green-300 hover:text-green-500 transition-all"><PlusIcon className="h-3 w-3" /></button>
                        <button onClick={() => removeFromCart(item.cartKey)} className="w-6 h-6 flex items-center justify-center rounded-lg ml-1 text-red-400 hover:bg-red-50 transition-all"><TrashIcon className="h-3 w-3" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── PAYMENT — updated section ──────────────────────────────── */}
            {cartItems.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2" style={{ background: 'linear-gradient(135deg,#f8f9fa,#fef2f2)' }}>
                  <ReceiptRefundIcon className="h-4 w-4" style={{ color: '#800020' }} />
                  <span className="text-sm font-extrabold text-gray-900">Payment</span>
                </div>
                <div className="p-4 space-y-3">

                  {/* Discount */}
                  <div className="flex items-center gap-2">
                    <label className="text-xs font-semibold text-gray-500 flex-shrink-0">Discount KES</label>
                    <input type="number" min="0" max={subtotal} value={discount || ''} onChange={e => setDiscount(Math.min(subtotal, Math.max(0, parseInt(e.target.value) || 0)))} placeholder="0"
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-xl text-xs text-center focus:outline-none transition-all"
                      onFocus={e => e.target.style.borderColor = '#800020'} onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
                  </div>

                  {/* Totals */}
                  <div className="space-y-1 border-t border-gray-100 pt-2">
                    <div className="flex justify-between text-xs text-gray-500"><span>Subtotal</span><span>KES {fmtNum(subtotal)}</span></div>
                    {discount > 0 && <div className="flex justify-between text-xs text-green-600 font-semibold"><span>Discount</span><span>- KES {fmtNum(discount)}</span></div>}
                    <div className="flex justify-between items-center pt-1">
                      <span className="text-sm font-extrabold text-gray-900">Total</span>
                      <span className="text-lg font-extrabold" style={{ color: '#800020' }}>KES {fmtNum(total)}</span>
                    </div>
                  </div>

                  {/* Payment mode tabs */}
                  <div className="grid grid-cols-3 gap-1.5 pt-1">
                    {[
                      { val: 'cash',    label: 'Cash',    icon: <BanknotesIcon className="h-3.5 w-3.5" />   },
                      { val: 'online',  label: 'Online',  icon: <CreditCardIcon className="h-3.5 w-3.5" />  },
                      { val: 'partial', label: 'Partial', icon: <span className="text-xs font-extrabold leading-none">½</span> },
                    ].map(({ val, label, icon }) => (
                      <button key={val} onClick={() => { setPayMode(val); setCashAmount(''); setCashRef(''); setOnlineRef(''); }}
                        className="flex flex-col items-center gap-1 py-2 rounded-xl border-2 text-xs font-bold transition-all"
                        style={{
                          borderColor: payMode === val ? '#800020' : '#e5e7eb',
                          background:  payMode === val ? '#fef2f2' : '#fff',
                          color:       payMode === val ? '#800020' : '#6b7280',
                        }}>
                        <span style={{ color: payMode === val ? '#800020' : '#9ca3af' }}>{icon}</span>
                        {label}
                      </button>
                    ))}
                  </div>

                  {/* CASH — optional amount received for change */}
                  {payMode === 'cash' && (
                    <div className="space-y-2 p-3 rounded-xl border" style={{ borderColor: '#bbf7d0', background: '#f0fdf4' }}>
                      <label className="block text-xs font-bold text-gray-500">Amount Received (optional)</label>
                      <input type="number" value={cashRef} onChange={e => setCashRef(e.target.value)}
                        placeholder={`KES ${fmtNum(total)}`}
                        className="w-full px-3 py-2 border border-gray-200 rounded-xl text-xs focus:outline-none transition-all bg-white"
                        onFocus={e => e.target.style.borderColor = '#16a34a'} onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
                      {changeAmount > 0 && (
                        <div className="flex justify-between items-center text-xs font-semibold text-green-700">
                          <span>Change to give back</span>
                          <span className="font-extrabold">KES {fmtNum(changeAmount)}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ONLINE — fires coming soon Swal, no inputs needed */}
                  {payMode === 'online' && (
                    <div className="p-3 rounded-xl border text-center" style={{ borderColor: '#bfdbfe', background: '#eff6ff' }}>
                      <p className="text-[11px] text-blue-600 font-medium">🚧 Paystack integration coming soon</p>
                      <p className="text-[10px] text-blue-400 mt-0.5">Clicking Complete will show payment details</p>
                    </div>
                  )}

                  {/* PARTIAL — cash amount, rest is online remainder */}
                  {payMode === 'partial' && (
                    <div className="space-y-2 p-3 rounded-xl border border-gray-200 bg-gray-50">
                      <label className="block text-xs font-bold text-gray-600">Cash Amount Paid Now</label>
                      <input type="number" min="0" max={total} value={cashAmount} onChange={e => setCashAmount(e.target.value)}
                        placeholder="e.g. 500"
                        className="w-full px-3 py-2 border border-gray-200 rounded-xl text-xs focus:outline-none transition-all bg-white"
                        onFocus={e => e.target.style.borderColor = '#800020'} onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
                      {cashNum > 0 && (
                        <div className="space-y-1 pt-1 border-t border-gray-200">
                          <div className="flex justify-between text-xs">
                            <span className="text-green-700 font-semibold flex items-center gap-1"><BanknotesIcon className="h-3 w-3" />Cash now</span>
                            <span className="font-bold text-green-700">KES {fmtNum(cashNum)}</span>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="text-blue-600 font-semibold flex items-center gap-1"><CreditCardIcon className="h-3 w-3" />Online remainder</span>
                            <span className="font-bold text-blue-600">KES {fmtNum(onlineNum)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── ACTION BUTTONS ── */}

                  {/* CASH: single Complete button */}
                  {payMode === 'cash' && (
                    <motion.button whileHover={{ scale: 1.02, boxShadow: '0 8px 24px rgba(128,0,32,0.3)' }} whileTap={{ scale: 0.97 }}
                      onClick={processSale} disabled={processing || !cartItems.length}
                      className="w-full py-3.5 rounded-2xl text-sm font-extrabold text-white shadow-lg transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      style={{ background: 'linear-gradient(135deg,#800020,#b30000)' }}>
                      {processing ? <><ArrowPathIcon className="h-4 w-4 animate-spin" />Processing…</> : <><CheckSolid className="h-4 w-4" />Complete Sale · KES {fmtNum(total)}</>}
                    </motion.button>
                  )}

                  {/* ONLINE: fires coming soon Swal */}
                  {payMode === 'online' && (
                    <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                      onClick={handleOnlineClick} disabled={!cartItems.length}
                      className="w-full py-3.5 rounded-2xl text-sm font-extrabold text-white shadow-lg transition-all disabled:opacity-60 flex items-center justify-center gap-2"
                      style={{ background: 'linear-gradient(135deg,#3b82f6,#2563eb)' }}>
                      <CreditCardIcon className="h-4 w-4" />Pay Online · KES {fmtNum(total)}
                    </motion.button>
                  )}

                  {/* PARTIAL: Record Cash first, then Complete Remainder */}
                  {payMode === 'partial' && (
                    <motion.button whileHover={{ scale: 1.02, boxShadow: '0 8px 24px rgba(22,163,74,0.3)' }} whileTap={{ scale: 0.97 }}
                      onClick={recordCashPortion} disabled={processing || !cartItems.length || cashNum <= 0}
                      className="w-full py-3.5 rounded-2xl text-sm font-extrabold text-white shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      style={{ background: 'linear-gradient(135deg,#16a34a,#15803d)' }}>
                      {processing ? <><ArrowPathIcon className="h-4 w-4 animate-spin" />Saving…</> : <><BanknotesIcon className="h-4 w-4" />Record Cash · KES {fmtNum(cashNum)} {cashNum > 0 && onlineNum > 0 ? `(KES ${fmtNum(onlineNum)} online pending)` : ''}</>}
                    </motion.button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {variantPickerProduct && <VariantPicker product={variantPickerProduct} onSelect={handleVariantSelected} onClose={() => setVariantPickerProduct(null)} />}
      </AnimatePresence>
      <AnimatePresence>
        {completedSale && <ReceiptModal sale={completedSale} onClose={() => setCompletedSale(null)} />}
      </AnimatePresence>

      <style>{`.line-clamp-2{display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;}`}</style>
    </div>
  );
};

export default Sale;